import { ReactElement, useContext, useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { AxiosError } from 'axios';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import styles from './Step11.module.scss';
import cms from '../../../data/cms.json';
import { ToggleField } from '../../ToggleField/ToggleField';
import { fetchResultData } from '../../../utils/externalDataHandling';

import { Step } from '../../Step/Step';
import { ErrorForm } from '../../ErrorForm/ErrorForm';
import { Box } from '../../Box/Box';
import { Button } from '../../Button/Button';
import { Grid } from '../../Grid/Grid';
import { Label } from '../../Label/Label';
import { Textarea } from '../../Textarea/Textarea';
import { Title } from '../../Title/Title';
import { AppContext } from '../../../context/AppContext';
import { CustomLocation } from '../../../types/common';
import { useLocalStorage } from '../../../hooks/useLocalStorageHook';

interface Step11Props {
    className?: string;
}

const feedbackKey = 'feedback';

export const Step11 = ({ className }: Step11Props): ReactElement | null => {
    const ctx = useContext(AppContext);
    const location: CustomLocation = useLocation();
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useLocalStorage(feedbackKey, '');

    useEffect(() => {
        localStorage.setItem('currentStep', '12');
        ctx.setIsBackButtonVisible(true);
    }, []);

    return (
        <Step title={cms.step11.title} text={cms.step11.text} className={cn(styles.wrapper, className)}>
            <Box loading={loading} data-test="finishBox">
                {error ? (
                    <Title tag="span" size="md" data-test="feedbackError">
                        {cms.common.feedbackErrorTitle}
                    </Title>
                ) : success ? (
                    <div className={styles.feedbackText} data-test="feedbackSuccess">
                        <Title tag="span" size="md" data-test="feedbackSuccessTitle">
                            {cms.common.feedbackSuccessTitle}
                        </Title>
                        <Button
                            variant="secondary"
                            onClick={() => window && window.open('https://www.simplea.cz', '_self')}
                            data-test="navigateToSimplea"
                        >
                            {cms.common.navigateToSimplea}
                        </Button>
                    </div>
                ) : (
                    <Formik
                        initialValues={{
                            leadSource: feedback?.leadSource || '',
                            rating: feedback?.rating || '',
                            message: feedback?.message || '',
                        }}
                        enableReinitialize
                        onSubmit={(values) => {
                            const state = location?.state;
                            const externalId = state?.ExternalId;

                            if (externalId) {
                                setLoading(true);

                                const body = {
                                    FeedbackText: values.message,
                                    OnlinePolicyExternalId: externalId,
                                    LeadSourceText: values.leadSource,
                                    ClientsSatisfactionLevel: values.rating ? parseInt(values.rating) : null,
                                };

                                setFeedback({
                                    leadSource: values.leadSource,
                                    rating: values.rating,
                                    message: values.message,
                                });

                                const result = fetchResultData('api/online/support/send-feedback', body);
                                result
                                    .then(() => {
                                        setSuccess(true);
                                        setLoading(false);
                                    })
                                    .catch((err: Error | AxiosError) => {
                                        console.error(err);
                                        setError(true);
                                        setLoading(false);
                                    });
                            } else {
                                console.error('ExternalId is missing');
                                setError(true);
                            }
                        }}
                    >
                        {() => (
                            <Form className={styles.form} data-test="feedbackForm">
                                <Label htmlFor="leadSource" data-test="feedbackFormTitle">
                                    {cms.step11.onlineBookingFeedbackQuestions.leadSource}
                                </Label>
                                <Grid cols="auto" className="mb-8" data-test="feedbackSource">
                                    <div className="toggle-list">
                                        <ToggleField
                                            name="leadSource"
                                            id="tv"
                                            variant="radio"
                                            value="Televize"
                                            optional
                                            data-test="feedbackSource[Televize]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="radio"
                                            variant="radio"
                                            value="Rádio"
                                            optional
                                            data-test="feedbackSource[Rádio]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="billboard"
                                            variant="radio"
                                            value="Billboard"
                                            optional
                                            data-test="feedbackSource[Billboard]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="social"
                                            variant="radio"
                                            value="Sociální síť"
                                            optional
                                            data-test="feedbackSource[Sociální síť]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="video"
                                            variant="radio"
                                            value="Video"
                                            optional
                                            data-test="feedbackSource[Video]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="banner"
                                            variant="radio"
                                            value="Banner"
                                            optional
                                            data-test="feedbackSource[Banner]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="google"
                                            variant="radio"
                                            value="Google"
                                            optional
                                            data-test="feedbackSource[Google]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="article"
                                            variant="radio"
                                            value="Článek na webu"
                                            optional
                                            data-test="feedbackSource[Článek na webu]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="recommendation"
                                            variant="radio"
                                            value="Na doporučení"
                                            optional
                                            data-test="feedbackSource[Na doporučení]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="mediator"
                                            variant="radio"
                                            value="Zprostředkovatel"
                                            optional
                                            data-test="feedbackSource[Zprostředkovatel]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="podcast"
                                            variant="radio"
                                            value="Podcast"
                                            optional
                                            data-test="feedbackSource[Podcast]"
                                        />
                                        <ToggleField
                                            name="leadSource"
                                            id="print"
                                            variant="radio"
                                            value="Tisk"
                                            optional
                                            data-test="feedbackSource[Tisk]"
                                        />
                                    </div>
                                </Grid>

                                <Label htmlFor="rating" data-test="ratingLabel">
                                    {cms.step11.onlineBookingFeedbackQuestions.rating}
                                </Label>
                                <Grid cols="auto" className="mb-8">
                                    <ToggleField
                                        name="rating"
                                        id="good"
                                        variant="radio"
                                        value="1"
                                        smileys
                                        optional
                                        data-test="rating[1]"
                                    />
                                    <ToggleField
                                        name="rating"
                                        id="neutral"
                                        variant="radio"
                                        value="5"
                                        smileys
                                        optional
                                        data-test="rating[5]"
                                    />
                                    <ToggleField
                                        name="rating"
                                        id="bad"
                                        variant="radio"
                                        value="10"
                                        smileys
                                        optional
                                        data-test="rating[10]"
                                    />
                                </Grid>

                                <Label htmlFor="message" data-test="messageLabel">
                                    {cms.step11.onlineBookingFeedbackQuestions.message}
                                </Label>
                                <Field
                                    className={styles.textarea}
                                    name="message"
                                    id="message"
                                    maxLenght={1000}
                                    component={Textarea}
                                    data-test="message"
                                />

                                <Button variant="primary" submit loading={loading} data-test="submitButton">
                                    {cms.common.submitButton}
                                </Button>
                            </Form>
                        )}
                    </Formik>
                )}
            </Box>

            <ErrorForm />
        </Step>
    );
};
